import { type FC, useState, useCallback, useRef, useEffect } from 'react';
import type { NodeProps } from 'reactflow';
import { useReactFlow } from 'reactflow';
import { useCanvasStore } from '@/stores/canvas.store';

export interface GroupNodeData {
  label: string;
  color?: string;
  width?: number;
  height?: number;
  locked?: boolean;
  colorKey?: string;
}

// 15 pastel colors — { id, bg, border } in HSL for readability.
// The `id` lets us persist the user's choice in data.colorKey.
const PASTEL_PALETTE: Array<{ key: string; bg: string; border: string }> = [
  { key: 'violet', bg: 'rgba(139, 92, 246, 0.08)', border: 'rgba(139, 92, 246, 0.4)' },
  { key: 'blue', bg: 'rgba(59, 130, 246, 0.08)', border: 'rgba(59, 130, 246, 0.4)' },
  { key: 'sky', bg: 'rgba(14, 165, 233, 0.08)', border: 'rgba(14, 165, 233, 0.4)' },
  { key: 'teal', bg: 'rgba(20, 184, 166, 0.08)', border: 'rgba(20, 184, 166, 0.4)' },
  { key: 'green', bg: 'rgba(34, 197, 94, 0.08)', border: 'rgba(34, 197, 94, 0.4)' },
  { key: 'lime', bg: 'rgba(132, 204, 22, 0.08)', border: 'rgba(132, 204, 22, 0.4)' },
  { key: 'yellow', bg: 'rgba(234, 179, 8, 0.1)', border: 'rgba(234, 179, 8, 0.45)' },
  { key: 'amber', bg: 'rgba(245, 158, 11, 0.1)', border: 'rgba(245, 158, 11, 0.45)' },
  { key: 'orange', bg: 'rgba(249, 115, 22, 0.1)', border: 'rgba(249, 115, 22, 0.45)' },
  { key: 'red', bg: 'rgba(239, 68, 68, 0.08)', border: 'rgba(239, 68, 68, 0.4)' },
  { key: 'pink', bg: 'rgba(236, 72, 153, 0.08)', border: 'rgba(236, 72, 153, 0.4)' },
  { key: 'fuchsia', bg: 'rgba(217, 70, 239, 0.08)', border: 'rgba(217, 70, 239, 0.4)' },
  { key: 'indigo', bg: 'rgba(99, 102, 241, 0.08)', border: 'rgba(99, 102, 241, 0.4)' },
  { key: 'slate', bg: 'rgba(100, 116, 139, 0.08)', border: 'rgba(100, 116, 139, 0.45)' },
  { key: 'stone', bg: 'rgba(120, 113, 108, 0.08)', border: 'rgba(120, 113, 108, 0.45)' },
];

function resolvePastel(data: GroupNodeData) {
  if (data.colorKey) {
    const found = PASTEL_PALETTE.find((p) => p.key === data.colorKey);
    if (found) return found;
  }
  // Back-compat: if only a legacy `color` hash is set, derive a stable palette entry from it.
  const seed = data.color || data.label || 'zone';
  const idx =
    Math.abs(seed.split('').reduce((a, c) => a + c.charCodeAt(0), 0)) % PASTEL_PALETTE.length;
  return PASTEL_PALETTE[idx];
}

const MIN_WIDTH = 200;
const MIN_HEIGHT = 120;

export const GroupNode: FC<NodeProps<GroupNodeData>> = ({ id, data, selected }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [showPalette, setShowPalette] = useState(false);
  const [label, setLabel] = useState(data.label || 'Nouvelle zone');
  const [size, setSize] = useState({ w: data.width || 400, h: data.height || 250 });
  const inputRef = useRef<HTMLInputElement>(null);
  const pastel = resolvePastel(data);
  const locked = data.locked === true;
  const { setNodes } = useReactFlow();

  useEffect(() => {
    setLabel(data.label || 'Nouvelle zone');
  }, [data.label]);

  useEffect(() => {
    setSize({ w: data.width || 400, h: data.height || 250 });
  }, [data.width, data.height]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const patchNode = useCallback(
    (patch: Partial<GroupNodeData> & { draggable?: boolean }) => {
      setNodes((nds) =>
        nds.map((n) => {
          if (n.id !== id) return n;
          const nextData = { ...n.data, ...patch };
          const next = { ...n, data: nextData };
          if (patch.draggable !== undefined) next.draggable = patch.draggable;
          if (patch.locked !== undefined) next.draggable = !patch.locked;
          return next;
        }),
      );
    },
    [id, setNodes],
  );

  const commitLabel = useCallback(() => {
    setIsEditing(false);
    patchNode({ label });
  }, [label, patchNode]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') commitLabel();
      if (e.key === 'Escape') {
        setLabel(data.label || 'Nouvelle zone');
        setIsEditing(false);
      }
    },
    [commitLabel, data.label],
  );

  const handleEdit = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditing(true);
    setShowPalette(false);
  }, []);

  const handleDelete = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      useCanvasStore.setState((state) => ({
        nodes: state.nodes.filter((n) => n.id !== id),
      }));
    },
    [id],
  );

  const handleToggleLock = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      patchNode({ locked: !locked });
    },
    [locked, patchNode],
  );

  const handleTogglePalette = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setShowPalette((v) => !v);
  }, []);

  const handlePickColor = useCallback(
    (key: string) => {
      patchNode({ colorKey: key });
      setShowPalette(false);
    },
    [patchNode],
  );

  // Resize logic
  const sizeRef = useRef(size);
  useEffect(() => {
    sizeRef.current = size;
  }, [size]);

  const startResize = useCallback(
    (e: React.MouseEvent, direction: string) => {
      if (locked) return;
      e.stopPropagation();
      e.preventDefault();
      const startX = e.clientX;
      const startY = e.clientY;
      const startW = size.w;
      const startH = size.h;

      const onMouseMove = (ev: MouseEvent) => {
        let newW = startW;
        let newH = startH;

        if (direction.includes('e')) newW = Math.max(MIN_WIDTH, startW + (ev.clientX - startX));
        if (direction.includes('w')) newW = Math.max(MIN_WIDTH, startW - (ev.clientX - startX));
        if (direction.includes('s')) newH = Math.max(MIN_HEIGHT, startH + (ev.clientY - startY));
        if (direction.includes('n')) newH = Math.max(MIN_HEIGHT, startH - (ev.clientY - startY));

        setSize({ w: newW, h: newH });
      };

      const onMouseUp = () => {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
        const final = sizeRef.current;
        setNodes((nds) =>
          nds.map((n) =>
            n.id === id
              ? {
                  ...n,
                  style: { ...n.style, width: final.w, height: final.h },
                  data: { ...n.data, width: final.w, height: final.h },
                }
              : n,
          ),
        );
      };

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    },
    [size, id, setNodes, locked],
  );

  const edgeHandleStyle = (
    cursor: string,
    pos: React.CSSProperties,
    vertical: boolean,
  ): React.CSSProperties => ({
    position: 'absolute',
    ...pos,
    width: vertical ? '100%' : 8,
    height: vertical ? 8 : '100%',
    cursor,
    zIndex: 20,
    background: 'transparent',
  });

  const cornerStyle = (cursor: string, pos: React.CSSProperties): React.CSSProperties => ({
    position: 'absolute',
    ...pos,
    width: 14,
    height: 14,
    cursor,
    zIndex: 21,
    background: selected ? 'var(--color-primary)' : pastel.border,
    borderRadius: 3,
    opacity: selected ? 1 : 0,
    transition: 'opacity 0.15s',
  });

  const actionBtn = (bg: string): React.CSSProperties => ({
    width: 24,
    height: 24,
    borderRadius: '50%',
    backgroundColor: bg,
    color: '#fff',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 13,
    lineHeight: 1,
    boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
  });

  return (
    <div
      style={{
        width: size.w,
        height: size.h,
        backgroundColor: pastel.bg,
        border: `2px ${locked ? 'solid' : 'dashed'} ${selected ? 'var(--color-primary)' : pastel.border}`,
        borderRadius: 'var(--radius-lg)',
        position: 'relative',
        pointerEvents: 'all',
      }}
    >
      {/* Action buttons — top right, visible on selection */}
      {selected && !isEditing && (
        <div
          className="nodrag nopan"
          style={{
            position: 'absolute',
            top: -12,
            right: -12,
            display: 'flex',
            gap: 4,
            zIndex: 30,
          }}
        >
          <button onClick={handleEdit} title="Modifier le titre" style={actionBtn('#3B82F6')}>
            ✎
          </button>
          <button
            onClick={handleTogglePalette}
            title="Couleur"
            style={actionBtn(pastel.border)}
          >
            🎨
          </button>
          <button
            onClick={handleToggleLock}
            title={locked ? 'Déverrouiller' : 'Verrouiller'}
            style={actionBtn(locked ? '#F59E0B' : '#6B7280')}
          >
            {locked ? '🔒' : '🔓'}
          </button>
          <button onClick={handleDelete} title="Supprimer" style={actionBtn('#EF4444')}>
            ×
          </button>
        </div>
      )}

      {/* Color palette popover */}
      {showPalette && selected && (
        <div
          className="nodrag nopan"
          style={{
            position: 'absolute',
            top: 22,
            right: -12,
            zIndex: 40,
            display: 'grid',
            gridTemplateColumns: 'repeat(5, 20px)',
            gap: 4,
            padding: 8,
            backgroundColor: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-lg)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {PASTEL_PALETTE.map((p) => (
            <button
              key={p.key}
              onClick={() => handlePickColor(p.key)}
              title={p.key}
              style={{
                width: 20,
                height: 20,
                borderRadius: 4,
                background: p.bg,
                border: `2px solid ${p.key === pastel.key ? 'var(--color-primary)' : p.border}`,
                cursor: 'pointer',
                padding: 0,
              }}
            />
          ))}
        </div>
      )}

      {/* Title bar */}
      <div
        onDoubleClick={() => !locked && setIsEditing(true)}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          padding: 'var(--spacing-xs) var(--spacing-sm)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--spacing-xs)',
          borderBottom: `1px ${locked ? 'solid' : 'dashed'} ${pastel.border}`,
          borderRadius: 'var(--radius-lg) var(--radius-lg) 0 0',
          backgroundColor: pastel.bg,
          cursor: locked ? 'default' : 'grab',
        }}
      >
        <span style={{ fontSize: 'var(--font-size-sm)', opacity: 0.6 }}>
          {locked ? '🔒' : '\u25A6'}
        </span>
        {isEditing ? (
          <input
            className="nodrag nopan"
            ref={inputRef}
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            onBlur={commitLabel}
            onKeyDown={handleKeyDown}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              outline: 'none',
              color: 'var(--color-text)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              padding: 0,
              width: '100%',
            }}
          />
        ) : (
          <span
            style={{
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              color: 'var(--color-text)',
              userSelect: 'none',
            }}
          >
            {label}
          </span>
        )}
      </div>

      {/* Resize handles — hidden when locked */}
      {!locked && (
        <>
          <div
            className="nodrag nopan"
            onMouseDown={(e) => startResize(e, 'e')}
            style={edgeHandleStyle('ew-resize', { top: 0, right: -4 }, false)}
          />
          <div
            className="nodrag nopan"
            onMouseDown={(e) => startResize(e, 'w')}
            style={edgeHandleStyle('ew-resize', { top: 0, left: -4 }, false)}
          />
          <div
            className="nodrag nopan"
            onMouseDown={(e) => startResize(e, 's')}
            style={edgeHandleStyle('ns-resize', { bottom: -4, left: 0 }, true)}
          />
          <div
            className="nodrag nopan"
            onMouseDown={(e) => startResize(e, 'n')}
            style={edgeHandleStyle('ns-resize', { top: -4, left: 0 }, true)}
          />
          <div
            className="nodrag nopan"
            onMouseDown={(e) => startResize(e, 'se')}
            style={cornerStyle('nwse-resize', { bottom: -7, right: -7 })}
          />
          <div
            className="nodrag nopan"
            onMouseDown={(e) => startResize(e, 'sw')}
            style={cornerStyle('nesw-resize', { bottom: -7, left: -7 })}
          />
          <div
            className="nodrag nopan"
            onMouseDown={(e) => startResize(e, 'ne')}
            style={cornerStyle('nesw-resize', { top: -7, right: -7 })}
          />
          <div
            className="nodrag nopan"
            onMouseDown={(e) => startResize(e, 'nw')}
            style={cornerStyle('nwse-resize', { top: -7, left: -7 })}
          />
        </>
      )}
    </div>
  );
};

export default GroupNode;
